import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Paper,
} from "@mui/material";
import { formatJpDateTime } from "../../utils/dateUtils";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import { updateMenuLinks } from "../../utils/menuLink";
import RequestStatus from "../../shared/request/RequestStatus";
import RequestDetailActionButton from "../../shared/RequestDetailActionButton";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";

const JobRequestDetail = ({
  userJobRequestInfo,
  userJobRequest,
  prefectures,
  contractRecruitingFlag,
  viewerType,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const hasRequest = !!userJobRequest?.id;
  const isDesktop = useIsDesktop();

  // textareaの改行設定
  const preLineLabels = ["remark", "previous_companies_and_reasons"];

  useEffect(() => {
    if (typeof userJobRequest?.recruiting_flag === "boolean") {
      setIsActive(userJobRequest.recruiting_flag);
    }
  }, [userJobRequest]);

  const updateRecruitingFlag = (newStatus) => {
    fetch("/user_job_request/toggle_recruiting", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ recruiting_flag: newStatus }),
    }).then((res) => {
      if (res.ok) {
        setIsActive(newStatus);
        updateMenuLinks(contractRecruitingFlag, newStatus);
        window.dispatchEvent(
          new CustomEvent("unread:accepting-committed", {
            detail: { acceptingJobs: Boolean(newStatus) },
          }),
        );
      } else {
        alert("ステータスの更新に失敗しました。");
      }
    });
  };

  const toggleStatus = useCallback(() => {
    if (isActive) {
      setModalOpen(true);
    } else {
      updateRecruitingFlag(true);
    }
  }, [isActive, setModalOpen, updateRecruitingFlag]);

  const handleConfirmStop = () => {
    updateRecruitingFlag(false);
    setModalOpen(false);
  };

  const handleCancelStop = () => {
    setModalOpen(false);
  };

  const rows = useMemo(
    () => [
      {
        label: "入社希望日",
        value: userJobRequest?.desired_joining_date
          ? new Date(userJobRequest.desired_joining_date).toLocaleDateString(
              "ja-JP",
            )
          : "未設定",
      },
      {
        label: "退職予定日",
        value: userJobRequest?.expected_retirement_date
          ? new Date(
              userJobRequest.expected_retirement_date,
            ).toLocaleDateString("ja-JP")
          : "未設定",
      },
      {
        label: "(額面)希望年収",
        value:
          userJobRequest?.expected_salary_min &&
          userJobRequest?.expected_salary_max
            ? `${userJobRequest.expected_salary_min}万円 〜 ${userJobRequest.expected_salary_max}万円`
            : userJobRequest?.expected_salary_min
              ? `${userJobRequest.expected_salary_min}万円 〜`
              : userJobRequest?.expected_salary_max
                ? `〜 ${userJobRequest.expected_salary_max}万円`
                : "未設定",
      },
      {
        label: "希望勤務地",
        value:
          Array.isArray(userJobRequest?.prefecture_ids) &&
          userJobRequest.prefecture_ids.length > 0 &&
          Array.isArray(prefectures) &&
          prefectures.length > 0
            ? userJobRequest.prefecture_ids
                .map(
                  (id) =>
                    prefectures.find((p) => p.id === id)?.name || `ID:${id}`,
                )
                .join("、")
            : "未設定",
      },
      {
        label: "希望雇用形態",
        value: !userJobRequest?.id
          ? "未設定"
          : (userJobRequestInfo?.desired_employment_type ?? "未設定"),
      },
      {
        label: "備考",
        labelKey: "remark",
        value: userJobRequest?.remark ?? "未設定",
      },
      {
        label: "(額面)今の年収",
        value: userJobRequest?.current_salary
          ? `${userJobRequest.current_salary}万円`
          : "未設定",
      },
      {
        label: "最終学歴",
        value: userJobRequestInfo?.final_academic_background?.trim()
          ? userJobRequestInfo.final_academic_background
          : "未設定",
      },
      {
        label: "保有資格",
        value:
          Array.isArray(userJobRequestInfo?.qualification) &&
          userJobRequestInfo.qualification.length > 0
            ? userJobRequestInfo.qualification.join("、")
            : "なし",
      },
      {
        label: "次で何回目の転職か",
        value:
          userJobRequest?.career_change_count != null
            ? `${userJobRequest.career_change_count}回目`
            : "未設定",
      },
      {
        label: (
          <>
            これまでの会社
            <br />
            退職理由(簡単に)
          </>
        ),
        labelKey: "previous_companies_and_reasons",
        value: userJobRequest?.previous_companies_and_reasons ?? "未設定",
      },
      {
        label: "今回の転職理由",
        value: userJobRequest?.current_change_reason ?? "未設定",
      },
    ],
    [userJobRequest, userJobRequestInfo, prefectures],
  );

  return (
    <Paper
      sx={{
        borderRadius: 2,
        p: { xs: 2, sm: 4 },
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{
          fontSize: "clamp(0.7rem, 4vw, 1.8rem)",
          alignItems: "left",
          lineHeight: 1.5,
          whiteSpace: "nowrap",
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        正社員雇用の転職先、派遣、アルバイト探し
      </Typography>

      {/* ステータス */}
      <RequestStatus
        viewerType={viewerType}
        type="job"
        isActive={isActive}
        hasRequest={hasRequest}
        onToggle={toggleStatus}
      />

      {/* 確認用モーダル */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title="転職先の募集停止"
        message="転職先の募集を停止すると求人情報の閲覧が不可能となります。"
        confirmLabel="募集を停止する"
      />

      {/* ユーザーにのみ表示 */}
      {viewerType !== "company" &&
        (hasRequest ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {isActive ? (
              <>
                募集要項とあなたのスキルシート詳細画面は<strong>公開中</strong>
                です。
                <br />
                転職先を募集中のみ、企業からの求人情報を閲覧することが可能です。
                <br />
                受付をOFFにすると求人情報の閲覧が不可となります。ご注意ください。
              </>
            ) : (
              <>
                現在、 転職先を募集は<strong>停止中</strong>です。
                <br />
                募集要項とあなたのスキルシート詳細画面は企業側には表示されません。
                <br />
                受付をONにすると、企業があなたの情報を閲覧できるようになります。
              </>
            )}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.8rem', sm: '0.875rem" },
            }}
          >
            募集要項がまだ作成されていません。
            <br />
            「編集する」ボタンから、まずは募集要項を登録してください。
          </Typography>
        ))}

      <Divider sx={{ mb: 3 }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">募集要項</Typography>

        {/* ユーザーにのみ表示 */}
        {viewerType !== "company" && (
          <Button
            variant="text"
            size="small"
            sx={{
              mb: 0.5,
              color: "#000",
              textTransform: "none",
              transition: "opacity 0.2s ease-in-out",
              "&:hover": { opacity: 0.7 },
              "&:focus": { opacity: 0.7 },
            }}
            onClick={() => (window.location.href = "/user_job_request/edit")}
          >
            編集する
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="#1976d2"
              viewBox="0 0 24 24"
            >
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
            </svg>
          </Button>
        )}
      </Box>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mb: 2, display: "block" }}
      >
        更新日時：{formatJpDateTime(userJobRequest?.updated_at)}
      </Typography>

      {isDesktop ? (
        <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#4b5563" : "#9ca3af",
                    color: "white",
                    width: "25%",
                  }}
                >
                  {row.label}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                    whiteSpace: preLineLabels.includes(row.labelKey)
                      ? "pre-line"
                      : undefined,
                    wordBreak: preLineLabels.includes(row.labelKey)
                      ? "break-all"
                      : undefined,
                  }}
                >
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
          <TableBody>
            {rows.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#4b5563" : "#9ca3af",
                      color: "white",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    {row.label}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                      whiteSpace: preLineLabels.includes(row.labelKey)
                        ? "pre-line"
                        : undefined,
                      wordBreak: preLineLabels.includes(row.labelKey)
                        ? "break-all"
                        : undefined,
                      fontSize: "0.8rem",
                    }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      {/* プロジェクト一覧に遷移 */}
      <RequestDetailActionButton
        viewerType={viewerType}
        hasRequest={hasRequest}
        isActive={isActive}
        buttonLabel="転職先を探してみる →"
        buttonUrl="/jobs"
        tooltip="現在転職先探しを停止しています"
      />
    </Paper>
  );
};

export default JobRequestDetail;
